<template>
  <div class="job-content-create">
    <common-card title="Groups" class="groups">
      <div slot="header-ops" class="f-r-vc header-ops">
        <div @click="() => openGroupEdit()" class="f-r-c-c group-add">
          <svg-icon icon-class="new-group" />
          <span style="font-size: 14px">New Group</span>
        </div>
      </div>
      <div class="group-list f-c">
        <div class="group-header f-r">
          <div class="table-row-status-indicator f-r">
            <div class="status-spot f-c-c-c">
              <svg-icon icon-class="scissor" />
            </div>
            <span class="status-title">Split Group By CD No.</span>
          </div>
          <div class="table-row-status-indicator f-r">
            <div class="status-spot f-c-c-c goods-hold-status"></div>
            <svg-icon icon-class="lock" class="svg-lock" />
            <span class="status-title">Group Pallets On Hold</span>
          </div>
        </div>
        <div class="f-r" style="gap: 10px; flex-wrap: wrap">
          <div
            class="group-item f-r-hsb f-r-vc hover-pointer"
            :class="{
              selected: item.name === curGroup,
              hold: item.hold === HOLD
            }"
            v-for="item in Object.values(goodsGroups)"
            :key="item.name"
            @click="onGroupClick(item.name)"
          >
            <svg-icon
              icon-class="lock"
              class="svg-lock"
              v-if="item.hold === HOLD"
            />
            <span class="title">{{ item.name }}</span>
            <svg-icon
              icon-class="pencil"
              class="modify hover-pointer"
              @click.native.prevent.stop="modifyGroup(item.name)"
            />
            <svg-icon
              icon-class="scissor"
              class="split hover-pointer"
              @click.native.prevent.stop="splitBySKU(item)"
            />
            <svg-icon
              icon-class="trash"
              class="delete hover-pointer"
              @click.native.prevent.stop="removeGroup(item)"
            />
          </div>
        </div>
      </div>
    </common-card>
    <el-transfer
      filterable
      v-model="curGroupGoodsIds"
      :data="availableGroupGoods"
      @change="onTransferChange($event)"
      :titles="['Packing List', curGroup || 'Not Selected']"
      v-loading="loadingPackingList"
      :filter-method="filterMethod"
    >
      <div class="item-label light-oswald" slot-scope="{ option }">
        <span class="first-row">{{ option.addressCode }}</span>
        <span class="second-row"
          ><span>{{ option.sku }}</span>
          <span>{{ option.shipmentMethod }}</span>
          <span>{{ option.shipmentNo }}</span>
          <span>{{ option.referenceNo }}</span></span
        >
      </div>
      <div slot="left-footer">
        <el-button
          type="text"
          class="footer-button"
          size="medium"
          @click="resetGroups"
        >
          Reset
        </el-button>
      </div>

      <div slot="right-footer">
        <el-button
          type="text"
          class="footer-button"
          size="medium"
          @click="autoGenerateGroups"
        >
          Auto Generate
        </el-button>
      </div>
    </el-transfer>
    <goods-group-edit ref="newGroupDialog" />
  </div>
</template>

<script>
// apis
import { queryGoods } from '@/api/goods'
// components
import CommonCard from '@/components/common/CommonCard.vue'
import GoodsGroupEdit from '@/components/goods/GoodsGroupEdit.vue'
// models
import { NormalPallet } from '@/core/models/pallet'
import { queryAddress } from '@/api/address'
import { HOLD, UNHOLD } from '@/core/models/constant'
// others
import isEmpty from 'lodash/isEmpty'

export default {
  name: 'JobContentCreate',
  props: {
    // containerId: {
    //   type: String,
    //   required: true
    // },
    goodQuery: {
      type: Object,
      required: true
    },
    groupsChanged: {
      type: Function,
      default: () => {}
    },
    originalGoodsGroupArr: {
      type: Array,
      default: () => []
    }
  },
  components: {
    CommonCard,
    GoodsGroupEdit
  },
  data() {
    return {
      HOLD,
      UNHOLD,
      curGroup: '',
      goodsMap: {},
      goodsGroups: {},
      loadingPackingList: false,
      addressMap: {}
    }
  },
  watch: {
    goodQuery: {
      handler() {
        if (!isEmpty(this.goodQuery)) {
          this.getPackingList()
        }
      },
      immediate: true
    },
    goodsGroups: {
      handler() {
        this.groupsChanged(this.goodsGroups, this.goodsMap)
      },
      deep: true
    },
    originalGoodsGroup: {
      handler() {
        if (!isEmpty(this.originalGoodsGroup)) {
          this.$set(this, 'goodsGroups', this.originalGoodsGroup)
          this.curGroup = Object.keys(this.goodsGroups)[0]
        }
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    curGroupGoodsIds: {
      get() {
        return this.goodsGroups?.[this.curGroup]?.goodsIds ?? []
      },
      set(value) {}
    },
    selectedGroupGoods() {
      if (isEmpty(this.goodsMap)) return []
      return this.curGroupGoodsIds.map(id => this.goodsMap[id])
    },
    groupedGoodsIds() {
      return Object.values(this.goodsGroups)
        .map(group => group.goodsIds)
        .flat(Infinity)
    },
    unGroupedGoodsIds() {
      return Object.values(this.goodsMap).filter(
        one => !this.groupedGoodsIds.includes(one.id)
      )
    },
    availableGroupGoods() {
      return this.unGroupedGoodsIds.concat(this.selectedGroupGoods)
    },
    originalGoodsGroup: {
      get() {
        const originalGoodsGroup = {}
        this.originalGoodsGroupArr.forEach(group => {
          const newGroup = {}
          newGroup.name = group.name
          newGroup.goodsIds = group.goods
          newGroup.address = this.addressMap[group.addressId]
          newGroup.hold = group.hold === 'True' ? HOLD : UNHOLD
          newGroup.priority = group.priority
          newGroup.quantity = group.quantity
          originalGoodsGroup[newGroup.name] = newGroup
        })
        return originalGoodsGroup
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    // ---------------------------- apis ----------------------------
    async getPackingList() {
      this.loadingPackingList = true
      const res = await queryGoods({ ...this.goodQuery, size: -1 })
      res.records.sort((one, two) => one.addressCode - two.addressCode)
      const goodsMap = Object.fromEntries(
        res.records.map(one => {
          one.key = one.id
          return [one.id, one]
        })
      )
      this.$set(this, 'goodsMap', goodsMap)
      // query address for each goods
      let addressList = res.records
        .map(one => one.addressId)
        .filter(one => !!one)
      addressList = [...new Set(addressList)]
      let addressRes
      if (addressList.length > 0) {
        addressRes = await queryAddress({
          addressIds: addressList,
          size: addressList.length
        })
      }
      Object.values(this.goodsMap).forEach(goods => {
        goods.address = (addressRes?.records ?? []).find(
          one => one.id === goods.addressId
        )
      })
      const addressMap = Object.fromEntries(
        (addressRes?.records ?? []).map(addr => [addr.id, addr])
      )
      this.$set(this, 'addressMap', addressMap)
      this.loadingPackingList = false
    },
    // ---------------------------- test data ----------------------------
    onTransferChange(e) {
      if (!this.curGroup) {
        this.$message.error('Please choose a group')
        return
      }
      this.$set(this.goodsGroups[this.curGroup], 'goodsIds', e)
    },
    // ---------------------------- group add ----------------------------
    onGroupClick(groupName) {
      this.curGroup = groupName
    },
    openGroupEdit(
      oldName = '',
      group = { hold: UNHOLD, priority: NormalPallet }
    ) {
      this.$refs.newGroupDialog.open(group, (values, done, error) => {
        const [flag, msg] = this.saveGroupEdit(oldName, values)
        if (flag) done()
        else error(msg)
      })
    },
    modifyGroup(groupName) {
      this.openGroupEdit(groupName, this.goodsGroups[groupName])
    },
    saveGroupEdit(oldName, group) {
      const [passed, err] = this.groupValidation(group, oldName)
      if (!passed) return [passed, err]
      if (oldName !== '') {
        const old = this.goodsGroups[oldName]
        delete this.goodsGroups[oldName]
        this.$set(this.goodsGroups, group.name, { ...old, ...group })
      } else {
        this.$set(this.goodsGroups, group.name, { ...group, goodsIds: [] })
      }
      this.onGroupClick(group.name)
      return [true, '']
    },
    groupValidation(newGroup, oldName) {
      if (newGroup.name !== oldName && newGroup.name in this.goodsGroups) {
        return [false, 'Same group already exists!']
      }
      if (newGroup.hold === UNHOLD && newGroup.addressId === undefined) {
        return [false, 'Please select an address for unhold group']
      }
      return [true, '']
    },
    removeGroup(group) {
      this.$confirm(`Remove group ${group.name}. Continue?`, 'Warning', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      })
        .then(() => {
          this.onGroupClick(group.name)
          this.$delete(this.goodsGroups, group.name)
          if (this.curGroup === group.name) {
            this.curGroup = Object.keys(this.goodsGroups)?.[0] ?? ''
          }
        })
        .catch(() => {})
    },
    // ---------------------------- group function ----------------------------
    async autoGenerateGroups() {
      // first group by address id
      // second group by address code
      // no address code and address in remain group
      await this.getPackingList()
      const address = {}
      Object.values(this.goodsMap).forEach(goods => {
        let key, hasAddress
        if (goods.address) {
          // has address
          key = goods.address.id
          hasAddress = true
        } else if (goods.addressCode) {
          // only address code
          if (
            (goods.addressCode && goods.addressCode.toLowerCase() === 'ups') ||
            (goods.shipmentMethod &&
              goods.shipmentMethod.toLowerCase() === 'ups')
          ) {
            key = 'UPS'
          } else if (
            (goods.addressCode &&
              goods.addressCode.toLowerCase() === 'fedex') ||
            (goods.shipmentMethod &&
              goods.shipmentMethod.toLowerCase() === 'fedex')
          ) {
            key = 'FedEx'
          } else {
            key = goods.addressCode
          }
        } else {
          // no address and address code
          key = 'remain'
        }
        if (!(key in address)) {
          address[key] = {
            name: hasAddress ? goods.address.code : key,
            goodsIds: [],
            address: hasAddress ? goods.address : null,
            hold: hasAddress ? UNHOLD : HOLD,
            priority: NormalPallet
          }
        }
        address[key].goodsIds.push(goods.key)
      })
      delete address.remain
      Object.keys(this.goodsGroups).forEach(key => {
        this.$delete(this.goodsGroups, key)
      })
      Object.keys(address).forEach(key => {
        const group = address[key]
        this.$set(this.goodsGroups, group.name, group)
      })
      this.curGroup = Object.keys(this.goodsGroups)[0]
    },
    resetGroups() {
      Object.keys(this.goodsGroups).forEach(key => {
        this.$delete(this.goodsGroups, key)
      })
    },
    checkGroupValid() {
      return this.unGroupedGoodsIds.length === 0
    },
    splitBySKU(group) {
      if (group.goodsIds.length === 0) return
      this.$confirm(`Split group ${group.name}. Continue?`, 'Warning', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      })
        .then(() => {
          this.$delete(this.goodsGroups, group.name)
          const newGroups = {}
          group.goodsIds.forEach(key => {
            const goods = this.goodsMap[key]
            const sku = goods.sku
            if (sku in newGroups) {
              newGroups[sku].goodsIds.push(goods.key)
            } else {
              newGroups[sku] = {
                name: sku,
                goodsIds: [goods.key],
                address: this.goodsMap[goods.key].address,
                hold: HOLD,
                priority: NormalPallet
              }
            }
          })
          Object.keys(newGroups).forEach(key => {
            const group = newGroups[key]
            if (key in this.goodsGroups) {
              group.goodsIds = [
                ...(this.goodsGroups[key].goodsIds ?? []),
                ...group.goodsIds
              ]
            }
            this.$set(this.goodsGroups, key, group)
          })
          this.onGroupClick(Object.values(newGroups)[0].name)
        })
        .catch(() => {})
    },
    filterMethod(query, item) {
      return item.sku.includes(query)
    },
    dumpGoodsGroup() {
      return this.goodsGroups
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/common.scss';

.job-content-create {
  @extend .f-c;

  .common-card {
    margin: 0;
  }

  .groups {
    margin-bottom: $home-card-padding-x;
    min-height: 75px;

    .header-ops {
      gap: 10px;

      .el-button {
        margin: 0;
      }
    }

    .group-add {
      font-size: $title-level-one;

      svg {
        filter: $svg-business-color;
      }

      &:hover {
        cursor: pointer;
        color: $business-color;
      }
    }

    .group-list {
      gap: 10px;
      min-height: 40px;
      margin-bottom: 10px;

      .group-header {
        gap: 20px;
      }

      .group-item {
        gap: 10px;
        border-radius: 3px;
        padding: 10px 20px;
        background-color: rgba($color: $business-color, $alpha: 0.1);

        .title {
          word-break: break-word;
          font-size: $title-level-three;
          font-weight: bold;
          margin-right: 10px;
          font-family: 'oswald bold', sans-serif;
        }

        .delete:hover,
        .split:hover,
        .modify:hover {
          filter: $svg-danger-color !important;
        }

        &.selected {
          color: white;
          background-color: rgba($business-color, 0.9) !important;

          .svg-icon {
            filter: $svg-white;
          }
        }

        &.hold {
          color: white;
          background-color: rgba($deep-warning, 0.9);
        }

        &:hover {
          background-color: rgba($business-color, 0.3);
        }
      }
    }
  }

  .el-transfer {
    @extend .f-r;

    justify-content: space-between;
    align-items: center;

    ::v-deep .el-transfer-panel__item {
      height: auto !important;
    }

    ::v-deep .el-transfer-panel__header {
      @extend .bold-oswald;
    }

    .el-checkbox-group {
      @extend .f-c;
    }

    .item-label {
      font-size: 16px;
      font-weight: 800;

      @extend .f-c;

      flex-wrap: wrap;

      .second-row {
        gap: 10px;
        font-size: $secondary-body-font-size;

        @extend .f-r-vc;
      }
    }

    ::v-deep .el-transfer-panel {
      width: 47% !important;
    }

    ::v-deep .el-transfer-panel__body {
      display: flex;
      min-height: 500px;
      flex-direction: column;

      .el-transfer-panel__list {
        flex-grow: 1;
      }
    }

    .footer-button {
      padding-left: 15px;
    }

    ::v-deep .el-transfer__buttons {
      @extend .f-c;

      gap: 20px;
      padding: 0;

      .el-transfer__button {
        margin: 0;
        padding: 8px 10px;

        span i {
          font-size: 24px;
        }
      }
    }
  }
}
</style>
